import { Link } from '@tanstack/react-router';
import { Shield, Workflow, ChevronRight, Users } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useAuthStore } from '@/stores/auth';

interface AdminCategory {
  id: string;
  name: string;
  desc: string;
  icon: typeof Workflow;
  to: string;
  badge: string;
  roles: ('SUPER_ADMIN' | 'TENANT_ADMIN' | 'TENANT_MEMBER')[];
}

const CATEGORIES: AdminCategory[] = [
  {
    id: 'iam',
    name: '身份与租户',
    desc: '管理租户(创建/停用/配额)与所有用户(角色/状态/密码)',
    icon: Users,
    to: '/admin/iam',
    badge: '超级管理员',
    roles: ['SUPER_ADMIN'],
  },
  {
    id: 'members',
    name: '成员管理',
    desc: '管理本租户下的成员账号(创建/停用/重置密码)',
    icon: Users,
    to: '/iam/members',
    badge: '租户管理员',
    roles: ['TENANT_ADMIN'],
  },
  {
    id: 'scripts',
    name: '脚本采集',
    desc: '统一管理所有定时执行的爬虫脚本与流水线',
    icon: Workflow,
    to: '/admin/scripts',
    badge: '共 1 个脚本',
    roles: ['SUPER_ADMIN', 'TENANT_ADMIN'],
  },
];

export function AdminPage() {
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'SUPER_ADMIN' || role === 'TENANT_ADMIN';
  const visible = CATEGORIES.filter((c) => (role ? c.roles.includes(role) : false));

  if (!isAdmin) {
    return (
      <div className="space-y-6 p-6 lg:p-8">
        <div className="rounded-xl border border-danger/30 bg-danger/5 p-6 text-sm text-danger">
          需要管理员权限才能访问此页面。
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 lg:p-8">
      <header>
        <div className="flex items-center gap-2 text-sm text-neutral-500">
          <Shield className="h-4 w-4 text-brand-500" />
          <span>管理员后台 · 仅授权人员可见</span>
        </div>
        <h1 className="mt-1 text-3xl font-semibold tracking-tight">管理员</h1>
        <p className="mt-1 text-sm text-neutral-600">
          应用管理中心 · 按分类组织底层运行的脚本、流水线与基础设施
        </p>
      </header>

      <section>
        <h2 className="mb-3 text-sm font-medium text-neutral-700">应用管理</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((c) => {
            const Icon = c.icon;
            return (
              <Link key={c.id} to={c.to} className="block">
                <Card className="group h-full cursor-pointer">
                  <CardContent className="flex h-full flex-col gap-3 p-5">
                    <div className="flex items-start justify-between">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50 text-brand-600 transition-colors group-hover:bg-brand-100">
                        <Icon className="h-5 w-5" />
                      </span>
                      <ChevronRight className="h-4 w-4 text-neutral-400 transition-transform group-hover:translate-x-0.5 group-hover:text-brand-500" />
                    </div>
                    <div>
                      <div className="text-base font-semibold text-neutral-900">{c.name}</div>
                      <p className="mt-1 text-xs leading-relaxed text-neutral-500">{c.desc}</p>
                    </div>
                    <div className="mt-auto text-[11px] text-neutral-400">{c.badge}</div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
